import { useEffect, useState, useCallback, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { CardState, Position, TabletopItemType, AnimationState } from '@/components/tabletop/types';
import CardBack from './CardBack';
import TabletopItem from '@/components/tabletop/components/TabletopItem';

interface CardProps {
  card: CardState & { animation?: AnimationState };
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onFlip?: (id: string) => void;
  onSelect?: (id: string, type: TabletopItemType, event: ReactMouseEvent) => void;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: (id: string) => void;
  onAnimationComplete?: (id: string) => void;
  isSelected?: boolean;
  isDragging?: boolean;
  className?: string;
  width?: number;
  height?: number;
  selectionColor?: string;
}

// Default card dimensions
const DEFAULT_CARD_WIDTH = 63;
const DEFAULT_CARD_HEIGHT = 88;

// Animation curve functions
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

export default function Card({
  card,
  onDragStart,
  onFlip,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  onAnimationComplete,
  isSelected = false,
  isDragging = false,
  className = '',
  width = DEFAULT_CARD_WIDTH,
  height = DEFAULT_CARD_HEIGHT,
  selectionColor
}: CardProps) {
  const { id, position, rotation, isFaceUp, imageUrl, backImageUrl, name, counters, animation, zIndex } = card;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Current animation state
  const [animatedPosition, setAnimatedPosition] = useState<Position>(position);
  const [animatedRotation, setAnimatedRotation] = useState<number>(rotation);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationType, setAnimationType] = useState<string>('none');
  const animationRef = useRef<number | null>(null);

  // Handle animation
  useEffect(() => {
    if (!animation || !animation.isAnimating) {
      setAnimatedPosition(position);
      setAnimatedRotation(rotation);
      setIsAnimating(false);
      setAnimationType('none');
      return;
    }

    setIsAnimating(true);
    setAnimationType(animation.animationType);
    const { startPosition, endPosition, startTime, duration, onComplete } = animation;
    
    // If it's a flip animation, we don't need to do the requestAnimationFrame loop
    // as the CSS transition will handle the visual effect
    if (animation.animationType === 'flip') {
      // For flip animations, we set a timeout to mark the animation as complete
      // after the duration, but we don't interfere with the CSS transition
      const timeoutId = setTimeout(() => {
        setIsAnimating(false);
        if (onAnimationComplete) {
          onAnimationComplete(id);
        }
        if (onComplete) {
          onComplete();
        }
      }, duration);
      
      return () => clearTimeout(timeoutId);
    }
    
    const animate = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Choose easing function based on animation type
      let easedProgress: number;
      if (animation.animationType === 'rotate') {
        easedProgress = easeOutBack(progress); // More bouncy effect for rotation
      } else {
        easedProgress = easeOutCubic(progress); // Smooth effect for movement
      }
      
      // Handle different animation types
      if (animation.animationType === 'rotate') {
        // For rotation, we store the rotation values in the x coordinate
        const startRotation = startPosition.x;
        const endRotation = endPosition.x;
        const currentRotation = startRotation + (endRotation - startRotation) * easedProgress;
        setAnimatedRotation(currentRotation);
      } else if (['deal', 'return', 'move'].includes(animation.animationType)) {
        // For movement animations, update position
        const currentPosition = {
          x: startPosition.x + (endPosition.x - startPosition.x) * easedProgress,
          y: startPosition.y + (endPosition.y - startPosition.y) * easedProgress
        };
        setAnimatedPosition(currentPosition);
      }
      
      if (progress < 1) {
        // Continue animation
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        setIsAnimating(false);
        setAnimationType('none');
        if (onAnimationComplete) {
          onAnimationComplete(id);
        }
        if (onComplete) {
          onComplete();
        }
      }
    };
    
    // Start animation (only for non-flip animations)
    animationRef.current = requestAnimationFrame(animate);
    
    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animation, position, rotation, id, onAnimationComplete]);

  // Handle front image loading
  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setError(false);
  }, []);

  // Handle front image error
  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setError(true);
  }, []);

  // Handle right click to flip
  const handleRightClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFlip) onFlip(id);
  }, [id, onFlip]);

  // Handle zoom
  const handleItemSelect = useCallback((itemId: string, type: TabletopItemType, event: ReactMouseEvent) => {
    // Call the parent select handler
    if (onSelect) onSelect(itemId, type, event);
  }, [onSelect]);

  // Determine if we should apply the flip transition
  // Always apply it for flip animations or when we're not animating
  const shouldApplyFlipTransition = animation?.animationType === 'flip' || !isAnimating;

  return (
    <TabletopItem
      id={id}
      type="card"
      position={isAnimating && animationType !== 'rotate' ? animatedPosition : position}
      rotation={isAnimating && animationType === 'rotate' ? animatedRotation : rotation}
      width={width}
      height={height}
      isSelected={isSelected}
      isDragging={isDragging}
      onSelect={handleItemSelect}
      onDragStart={onDragStart}
      onMouseEnter={itemId => onMouseEnter?.(itemId)}
      onMouseLeave={itemId => onMouseLeave?.(itemId)}
      className={`${className} ${isAnimating ? 'pointer-events-none z-50' : ''}`}
      zIndex={zIndex}
      selectionColor={selectionColor}
    >
      {/* Card container with 3D flip effect */}
      <div 
        className={`relative w-full h-full transform-style-preserve-3d ${isAnimating ? 'shadow-xl' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          transition: shouldApplyFlipTransition ? 'transform 0.6s ease-in-out' : 'none',
          transform: isFaceUp ? 'rotateY(0deg)' : 'rotateY(180deg)',
          backfaceVisibility: 'hidden'
        }}
        onContextMenu={handleRightClick}
      >
        {/* Card front */}
        <div 
          className="absolute w-full h-full backface-hidden bg-white rounded-md overflow-hidden"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(0deg)',
          }}
        >
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-300 rounded-md">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          
          {/* Error state */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-100 text-red-500 rounded-md">
              <span className="text-xs">Error</span>
              <span className="text-xs mt-1">FRONT</span>
            </div>
          )}
          
          {/* Front image */}
          <img
            src={imageUrl}
            alt={name || 'Card Front'}
            className="w-full h-full object-cover bg-gray-100 rounded-md"
            draggable={false}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ opacity: isLoading ? 0 : 1 }}
          />
          
          {/* Card counters (if any) */}
          {counters && Object.entries(counters).map(([type, count]) => (
            count > 0 && (
              <div
                key={`${id}-${type}`}
                className="absolute top-1 right-1 bg-black bg-opacity-70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >
                {count}
              </div>
            )
          ))}
        </div>
        
        {/* Card back */}
        <div
          className="absolute w-full h-full backface-hidden bg-white rounded-md overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <CardBack
            backImageUrl={backImageUrl}
          />
        </div>
      </div>
    </TabletopItem>
  );
} 
